import { STATUS_CODE } from '@kc-console/core';

import { printGuardLog } from '../../utils/printGuardLog';
import {
  createServerTraceGroupId,
  getServerTraceCategory,
  setServerTraceTerminalGuard,
  startServerTraceSpan,
} from '../../utils/serverTrace/store';

import type { BaseState, ServiceAppState } from '@kc-console/context';
import type { AnyOfGuard, Guard } from '../../types';
import type { ServerTraceOutcome } from '../../utils/serverTrace/types';

const isFulfilled = <T>(result: PromiseSettledResult<T>): result is PromiseFulfilledResult<T> =>
  result.status === 'fulfilled';

const getGuardOutcome = <T extends BaseState | ServiceAppState>(
  state: Partial<T>,
): ServerTraceOutcome => {
  if (state.redirect) return 'redirect';
  if (state.status && state.status !== STATUS_CODE.OK) return 'failed_status';
  return 'success';
};

export const combineGuardsParallel = <T extends BaseState | ServiceAppState>(
  guards: AnyOfGuard[],
): Guard<T> => {
  return async params => {
    const { context, state } = params;
    const groupId = createServerTraceGroupId(context.req, 'parallel');

    let newState = { ...state };

    const results = await Promise.allSettled(
      guards.map(async guard => {
        const guardName = guard.displayName || guard.name;
        const span = startServerTraceSpan(context.req, {
          name: guardName,
          category: getServerTraceCategory(guard.traceCategory),
          execution: 'parallel',
          groupId,
        });

        try {
          const fulfilledState = await (guard as unknown as Guard<T>)({
            ...params,
            state: params.state as T,
          });
          const mergedState = { ...state, ...fulfilledState };
          span.end(getGuardOutcome(mergedState), mergedState.status);
          return fulfilledState;
        } catch (error) {
          span.end('fail_open');
          throw error;
        }
      }),
    );

    const fulfilledResults = results
      .map((result, index) => ({ result, guard: guards[index], index }))
      .filter(({ result }) => isFulfilled(result));

    const fulfilledStates = fulfilledResults.map(({ result, guard }) => {
      const fulfilledState = (result as PromiseFulfilledResult<Partial<T>>).value;

      printGuardLog({
        context: params.context,
        state: { ...state, ...fulfilledState },
        guardName: guard.displayName || guard.name,
      });

      return fulfilledState;
    });

    let terminalGuardName = '';

    fulfilledStates.forEach((fulfilledState, index) => {
      if (newState.status && newState.status !== STATUS_CODE.OK) return;
      if (newState.redirect) return;

      newState = { ...newState, ...fulfilledState };

      if (newState.redirect || (newState.status && newState.status !== STATUS_CODE.OK)) {
        const guard = fulfilledResults[index]?.guard;
        terminalGuardName = guard?.displayName || guard?.name || '';
      }
    });

    setServerTraceTerminalGuard(context.req, terminalGuardName);

    return newState;
  };
};
